import { isNil, snakeCase } from "lodash";
import { GQL, GQLField, GQLFilters, GQLQuery, GQLFieldSettings, GQLSort } from "../types/GQL";

type GQLBuilderOperator =
  | "contains"
  | "notContains"
  | "equal"
  | "notEqual"
  | "startsWith"
  | "lessThan"
  | "isBefore"
  | "greaterThan"
  | "isAfter"
  | "isBetween"
  | "isAny"
  | "notAny"
  | "isNull"
  | "isNotNull";

type GQLBuilderFieldValue = string | string[] | number | number[] | boolean | null | undefined;

const anyValueOperators: GQLBuilderOperator[] = ["isNull", "isNotNull"];

function shouldThrowOutField(
  fieldName: string | null | undefined,
  operator: GQLBuilderOperator,
  fieldValue: GQLBuilderFieldValue,
) {
  // field name checks
  if (fieldName === "") return true;
  if (isNil(fieldName)) return true;

  // if operator is an any value operator, skip value checks
  if (anyValueOperators.includes(operator)) return false;

  // field value checks
  if (isNil(fieldValue)) return true;
  if (typeof fieldValue === "string" && fieldValue.length === 0) return true;
  if (Array.isArray(fieldValue) && fieldValue.length === 0) return true;

  // Let anything else through
  return false;
}

function makeGQLFilterField(fieldName: string, operator: GQLBuilderOperator, value?: GQLBuilderFieldValue): GQLField {
  // conditional here to facilitate the "equal" operator, which is implied by simply passing the value instead of an operator object
  const operatorObject =
    operator == "equal"
      ? value
      : {
          [snakeCase(operator)]: value,
        };
  return {
    [fieldName]: operatorObject,
  } as GQLField;
}

class GQLBuilderFilters {
  private gqlFilters: GQLFilters = {};
  private filterNameCounter = 1;

  /**
   * Add field to filter
   * @param fieldName
   * @param operator
   * @param value
   */
  field(fieldName: string, operator: GQLBuilderOperator, value?: GQLBuilderFieldValue, filterName?: string | number) {
    if (value === undefined) value = null;
    if (shouldThrowOutField(fieldName, operator, value)) return this;

    // if no filter name is supplied, generate one
    const filterNameToUse = filterName?.toString() ?? (this.filterNameCounter++).toString();
    const filter = {
      [filterNameToUse]: makeGQLFilterField(fieldName, operator, value),
    } as GQLFilters;

    this.gqlFilters = {
      ...this.gqlFilters,
      ...filter,
    };

    return this;
  }

  /**
   * @private
   */
  build(): GQL {
    return {
      filters: this.gqlFilters,
    };
  }
}

class GQLBuilderSort {
  private gqlSort: GQLSort = [];

  /**
   * Add field to sort
   * @param fieldName
   * @param order
   */
  field(fieldName: string, order: "asc" | "desc") {
    this.gqlSort.push({ [fieldName]: order });

    return this;
  }

  /**
   * @private
   */
  build(): GQL {
    return {
      sort: this.gqlSort,
    };
  }
}

class GQLQueryBuilderAnd {
  private conditions: GQLQuery[] = [];

  /**
   * Add field to group
   * @param fieldName
   * @param operator
   * @param value
   */
  field(fieldName: string, operator: GQLBuilderOperator, value?: GQLBuilderFieldValue): GQLQueryBuilderAnd {
    if (value === undefined) value = null;
    if (shouldThrowOutField(fieldName, operator, value)) return this;

    this.conditions.push(makeGQLFilterField(fieldName, operator, value));

    return this;
  }

  /**
   * All records could match conditions
   * @param builder add fields to 'or' (should) group
   */
  or(builder: (or: GQLQueryBuilderOr) => void): GQLQueryBuilderAnd {
    const or = new GQLQueryBuilderOr();

    builder(or);

    this.conditions.push(or.build());

    return this;
  }

  /**
   * @private
   */
  build(): GQLQuery {
    return {
      and: this.conditions,
    };
  }
}

class GQLQueryBuilderOr {
  private conditions: GQLQuery[] = [];

  /**
   * Add field to group
   * @param fieldName
   * @param operator
   * @param value
   */
  field(fieldName: string, operator: GQLBuilderOperator, value?: GQLBuilderFieldValue): GQLQueryBuilderOr {
    if (value === undefined) value = null;
    if (shouldThrowOutField(fieldName, operator, value)) return this;

    this.conditions.push(makeGQLFilterField(fieldName, operator, value));

    return this;
  }

  /**
   * All records must match conditions
   * @param builder add fields to 'and' (must) group
   */
  and(builder: (and: GQLQueryBuilderAnd) => void): GQLQueryBuilderOr {
    const and = new GQLQueryBuilderAnd();

    builder(and);

    this.conditions.push(and.build());

    return this;
  }

  /**
   * @private
   */
  build(): GQLQuery {
    return {
      or: this.conditions,
    };
  }
}

class GQLQueryBuilder {
  private gqlQuery: GQL = {};

  /**
   * All records must match conditions
   * @param builder add fields to 'and' (must) group
   */
  and(builder: (and: GQLQueryBuilderAnd) => void) {
    const and = new GQLQueryBuilderAnd();

    builder(and);

    this.gqlQuery = {
      query: { ...this.gqlQuery.query, ...and.build() },
    };

    return this;
  }

  /**
   * All records could match conditions
   * @param builder add fields to 'or' (should) group
   */
  or(builder: (or: GQLQueryBuilderOr) => void) {
    const or = new GQLQueryBuilderOr();

    builder(or);

    this.gqlQuery = {
      query: { ...this.gqlQuery.query, ...or.build() },
    };

    return this;
  }

  /**
   * @private
   */
  build(): GQL {
    return this.gqlQuery;
  }
}

class GQLBuilderFieldSettings {
  private gqlFieldSettings: GQLFieldSettings = {};

  /**
   * Add field settings
   * @param fieldName
   * @param settings
   */
  field(fieldName: string, settings: { weight: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 }) {
    this.gqlFieldSettings = {
      ...this.gqlFieldSettings,
      [fieldName]: { weight: settings.weight as number },
    };

    return this;
  }

  /**
   * @private
   */
  build(): GQL {
    return {
      field_settings: this.gqlFieldSettings,
    };
  }
}

/**
 * Generate GQL with a composable query builder syntax
 * @example
 * const gql = new GQLBuilder()
 *   .size(10)
 *   .filters(filter => {
 *     filter
 *       .field("firstName", "equal", "John")
 *       .field("lastName", "equal", "Doe")
 *   });
 * // Match all people named John Doe
 */
export class GQLBuilder {
  private gql: GQL = {};

  /**
   * Moves the query cursor for pagination/infinite scroll
   * @param cursor the index from which to start grabbing results from
   * @throws Will throw if called more than once.
   * @example
   * const gql = new GQLBuilder().from(50);
   */
  from(cursor: number): GQLBuilder {
    if (this.gql.from) throw Error("Method 'from' may only be called once.");

    this.gql = {
      ...this.gql,
      from: cursor,
    };

    return this;
  }

  /**
   * Limits the number of results
   * @param size the number of results to return
   * @throws Will throw if called more than once.
   * @example
   * const gql = new GQLBuilder().size(10);
   */
  size(size: number): GQLBuilder {
    if (this.gql.size) throw Error("Method 'size' may only be called once.");

    this.gql = {
      ...this.gql,
      size,
    };

    return this;
  }

  /**
   * Sorts the results by field
   * @param builder add sort fields with sort prop
   * @throws Will throw if called more than once.
   * @example
   * const gql = new GQLBuilder()
   *   .sort(sort => {
   *     sort
   *       .field("id", "asc")
   *       .field("name", "desc")
   *   })
   * // Sort by ascending id and descending name
   */
  sort(builder: (sort: GQLBuilderSort) => void): GQLBuilder {
    if (this.gql.sort) throw Error("Method 'sort' may only be called once.");

    const sort = new GQLBuilderSort();

    builder(sort);

    this.gql = {
      ...this.gql,
      ...sort.build(),
    };

    return this;
  }

  /**
   * Filter results by field, supports nesting in and/or groups
   * @param builder add query fields or and/or groups with query prop
   * @throws Will throw if called more than once.
   * @example
   * const gql = new GQLBuilder()
   *   .query(query => {
   *     query
   *       .and(and => {
   *         and
   *           .field("firstName", "equal", "John")
   *           .or(or => {
   *             or
   *               .field("email", "equal", "john.doe@gmail.com")
   *               .field("phone", "equal", "1234567890")
   *           })
   *       })
   *   });
   * // Matches people named John that have the specified email or phone number
   */
  query(builder: (query: GQLQueryBuilder) => void): GQLBuilder {
    if (this.gql.query) throw Error("Method 'query' may only be called once.");

    const query = new GQLQueryBuilder();

    builder(query);

    this.gql = {
      ...this.gql,
      ...query.build(),
    };

    return this;
  }

  /**
   * Nest filters using a custom filter expression syntax, depends on filters()
   * @param expression use filter names and `AND`, `OR`, and `()` to construct the expression
   * @throws Will throw if called more than once.
   * @example
   * new GQLBuilder()
   *   .filtersExpression("1 AND (2 OR 3)")
   *   .filters(filter => {
   *     filter
   *       .field("firstName", "equal", "John", "1")
   *       .field("email", "equal", "john.doe@gmail.com", "2")
   *       .field("phone", "equal", "1234567890", "1")
   *   })
   * // Matches people named John that have the specified email or phone number
   */
  filtersExpression(expression: string): GQLBuilder {
    if (this.gql.filters_expression) throw Error("Method 'filtersExpression' may only be called once.");

    this.gql = {
      ...this.gql,
      filters_expression: expression,
    };

    return this;
  }

  /**
   * @param builder add filter fields with filter prop
   * @throws Will throw if called more than once.
   * @example
   * new GQLBuilder()
   *   .filters(filter => {
   *     filter
   *       .field("firstName", "equal", "John", "1")
   *       .field("email", "equal", "john.doe@gmail.com", "2")
   *       .field("phone", "equal", "1234567890", "1")
   *   })
   * // Matches people named John that have the specified email and phone number
   */
  filters(builder: (filters: GQLBuilderFilters) => void): GQLBuilder {
    if (this.gql.filters) throw Error("Method 'filters' may only be called once.");

    const filters = new GQLBuilderFilters();

    builder(filters);

    this.gql = {
      ...this.gql,
      ...filters.build(),
    };

    return this;
  }

  /**
   * Quickly search for text across a list of fields
   * @param query The text query to be found in list of fields
   * @param fields The list of fields you're searching for text in
   * @param nestedFieldsExtIds TODO: what is this?
   * @example
   * const gql = new GQLBuilder()
   *   .fullText("John", ["firstName", "lastName"])
   * // Search `firstName` and `lastName` fields for "John"
   */
  fullText(query: string, fields: string[], nestedFieldsExtIds?: number[]): GQLBuilder {
    if (this.gql.full_text) throw Error("Method 'fullText' may only be called once.");

    this.gql = {
      ...this.gql,
      full_text: {
        keyword: query,
        fields: {
          standard_fields: fields,
          nested_fields_ext_ids: nestedFieldsExtIds,
        },
      },
    };

    return this;
  }

  /**
   * @param builder add field settings with fieldSettings prop
   * @throws Will throw if called more than once.
   * @example
   * new GQLBuilder()
   *   .fieldSettings(fieldSettings => {
   *     fieldSettings
   *       .field("firstName", { weight: 1 })
   *       .field("lastName", { weight: 2 })
   *   })
   * // Sets the weights
   */
  fieldSettings(builder: (fieldSettings: GQLBuilderFieldSettings) => void): GQLBuilder {
    if (this.gql.field_settings) throw Error("Method 'filters' may only be called once.");

    const fieldSettings = new GQLBuilderFieldSettings();

    builder(fieldSettings);

    this.gql = {
      ...this.gql,
      ...fieldSettings.build(),
    };

    return this;
  }

  /**
   * Called last to return the completed GQL object
   */
  build(): GQL {
    return this.gql;
  }
}
